import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  DYNAMIC_VIEW_DEFAULTS_KEY,
  DynamicViewDefaultsService
} from '@konnektu/dynamic/common';
import { injectShowTextTuiAlert } from '@konnektu/helpers';
import { equal, from, MetastoreService } from '@konnektu/metastore';
import {
  DefaultDialogComponent,
  injectTuiDialogContext
} from '@konnektu/taiga';
import { TranslateModule } from '@ngx-translate/core';
import { TuiLoader } from '@taiga-ui/core';
import { EditorComponent } from 'ngx-monaco-editor-v2';
import { saveDynamicViewDefaults } from './save-dynamic-view-defaults';

const EMPTY_DEFAULTS = `{\n  "section": {},\n  "components": {}\n}`;

@Component({
  selector: 'knk-dynamic-view-defaults-dialog',
  imports: [
    FormsModule,
    DefaultDialogComponent,
    EditorComponent,
    TuiLoader,
    TranslateModule
  ],
  templateUrl: './dynamic-view-defaults-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicViewDefaultsDialogComponent {
  private readonly context = injectTuiDialogContext<void>();
  private readonly metastore = inject(MetastoreService);
  private readonly viewDefaults = inject(DynamicViewDefaultsService);
  private readonly showTextAlert = injectShowTextTuiAlert();

  private row: { Id: string } | null = null;

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly value = signal(EMPTY_DEFAULTS);

  protected readonly monacoOptions = {
    language: 'json',
    automaticLayout: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fixedOverflowWidgets: true
  };

  constructor() {
    this.metastore
      .find<{ Id: string; Key: string; Value: string }>(
        from('UIConfig')
          .select(['Id', 'Key', 'Value'])
          .where(equal('Key', DYNAMIC_VIEW_DEFAULTS_KEY))
      )
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (found) => {
          this.row = found;
          this.value.set(this.format(found?.Value));
          this.loading.set(false);
        },
        error: () => this.loading.set(false)
      });
  }

  protected save() {
    this.saving.set(true);
    try {
      saveDynamicViewDefaults(this.metastore, this.row, this.value()).subscribe(
        {
          next: () => {
            this.viewDefaults.reload();
            this.showTextAlert('dynamicEditor.defaultsSaved', 'positive');
            this.context.completeWith();
          },
          error: () => {
            this.saving.set(false);
            this.showTextAlert('dynamicEditor.saveError', 'negative');
          }
        }
      );
    } catch {
      this.saving.set(false);
      this.showTextAlert('dynamicEditor.defaultsInvalidJson', 'negative');
    }
  }

  protected cancel() {
    this.context.completeWith();
  }

  private format(value: string | undefined) {
    if (!value) return EMPTY_DEFAULTS;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
}
